import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ISO_LANGUAGES } from '../../data/languages';

interface LanguageSelectorProps {
  selectedLanguages: string[];
  onLanguagesChange: (languages: string[]) => void;
  placeholder?: string;
}

export default function LanguageSelector({ 
  selectedLanguages, 
  onLanguagesChange, 
  placeholder 
}: LanguageSelectorProps) {
  const { t } = useTranslation();
  const defaultPlaceholder = placeholder || t('language.selectLanguages');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const getLanguageName = (code: string) => {
    // First try to get translation using the language code
    const translationKey = `languages.${code}`;
    const translated = t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
    
    // Fallback to ISO language data
    const language = ISO_LANGUAGES.find(lang => lang.code === code);
    if (language) {
      return language.nativeName || language.name;
    }
    
    return code;
  };

  const getLanguageDisplayName = (language: { code: string; name: string; nativeName?: string }) => {
    // First try to get translation using the language code
    const translationKey = `languages.${language.code}`;
    const translated = t(translationKey);
    if (translated !== translationKey) {
      return translated;
    }
    
    // Fallback to native name or English name
    return language.nativeName || language.name;
  };

  const filteredLanguages = ISO_LANGUAGES.filter(lang => {
    const translatedName = getLanguageDisplayName(lang);
    return (
      lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lang.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lang.nativeName && lang.nativeName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      translatedName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }).sort((a, b) => {
    return getLanguageDisplayName(a).localeCompare(getLanguageDisplayName(b));
  });

  const handleLanguageToggle = (languageCode: string) => {
    if (selectedLanguages.includes(languageCode)) {
      // Prevent removing the last language
      if (selectedLanguages.length > 1) {
        onLanguagesChange(selectedLanguages.filter(code => code !== languageCode));
      }
    } else {
      onLanguagesChange([...selectedLanguages, languageCode]);
    }
  };

  const handleRemoveLanguage = (languageCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Prevent removing the last language
    if (selectedLanguages.length > 1) {
      onLanguagesChange(selectedLanguages.filter(code => code !== languageCode));
    }
  };


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.language-selector')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="language-selector relative">
      <div
        className="min-h-[42px] w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 px-3 py-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1 items-center">
          {selectedLanguages.length === 0 ? (
            <span className="text-gray-400 dark:text-gray-500">{defaultPlaceholder}</span>
          ) : (
            selectedLanguages.map(code => (
              <span
                key={code}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-sm rounded-md"
              >
                {getLanguageName(code)}
                {selectedLanguages.length > 1 && (
                  <button
                    onClick={(e) => handleRemoveLanguage(code, e)}
                    className="hover:bg-blue-200 dark:hover:bg-blue-800 rounded-full p-0.5"
                    title={t('language.removeLanguage')}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))
          )}
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <input
              type="text"
              placeholder={t('language.searchLanguages')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredLanguages.length === 0 ? (
              <div className="p-2 text-sm text-gray-500 dark:text-gray-400">
                {t('language.noLanguagesFound')}
              </div>
            ) : (
              filteredLanguages.map(language => (
                <div
                  key={language.code}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center justify-between ${
                    selectedLanguages.includes(language.code) 
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                      : 'text-gray-900 dark:text-gray-100'
                  }`}
                  onClick={() => handleLanguageToggle(language.code)}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {getLanguageDisplayName(language)}
                    </span>
                    {language.nativeName && language.nativeName !== getLanguageDisplayName(language) && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {language.nativeName}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                    {language.code}
                  </span>
                  {selectedLanguages.includes(language.code) && (
                    <div className="ml-2 h-4 w-4 bg-blue-600 rounded-full flex items-center justify-center">
                      <div className="h-2 w-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}